import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { Chip, Icon, IconButton, TableColumnType, TableInstance } from '@elipssolution/harfang';
import { mdiDelete } from '@mdi/js';
import { useCallback, useEffect, useMemo } from 'react';

import AddUserCustomerFileRelationsForm from './AddUserCustomerFileRelationsForm';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import SettingsTable from '../../../../components/SettingsTable';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import {
	FETCH_INTERNAL_USER_CUSTOMER_FILE_RELATIONS_BY_USER,
	FetchInternalUserCustomerFileRelationsByUserType,
	REMOVE_INTERNAL_USER_CUSTOMER_FILE_RELATION,
	RemoveInternalUserCustomerFileRelationType,
} from '../../../api/internalUserCustomerFile';
import { InternalUserCustomerFileRelationType } from '../../../types/relation';
import { AddInternalUserRelationWithCustomerFilesType, AddInternalUserType, UserType } from '../../../types/user';
import { useUserCustomerFileRelationContext } from '../../UserCustomerFileRelationProvider';

type CustomerFileTableProps = {
	isAdministrator?: boolean;
	user: UserType;
	tableInstance: TableInstance;
};

const CustomerFilesByUserTable = ({ isAdministrator, user, tableInstance }: CustomerFileTableProps) => {
	const { id: userId } = user;

	const { push } = useSettingsDialog();
	const { userCustomerFileRelations, setUserCustomerFileRelations } = useUserCustomerFileRelationContext();
	const { confirmAction } = useConfirmationDialogContext();

	const [fetchCustomerFilesByUser, { networkStatus, refetch }] =
		useLazyQuery<FetchInternalUserCustomerFileRelationsByUserType>(
			FETCH_INTERNAL_USER_CUSTOMER_FILE_RELATIONS_BY_USER,
			{
				notifyOnNetworkStatusChange: true,
				variables: {
					userId,
				},
			},
		);

	const [
		removeInternalUserCustomerFileRelation,
		{ called: isRemoveInternalUserCustomerFileRelationCalled, reset: resetRemoveInternalUserCustomerFileRelation },
	] = useMutation<RemoveInternalUserCustomerFileRelationType>(REMOVE_INTERNAL_USER_CUSTOMER_FILE_RELATION, {
		onCompleted: ({ removeInternalUserCustomerFileRelation: removeInternalUserCustomerFileRelationData }) => {
			const { id: removedCustomerFileId } = removeInternalUserCustomerFileRelationData;

			if (userCustomerFileRelations.length > 0) {
				setUserCustomerFileRelations((prevValues) => {
					if ('user' in prevValues[0]) {
						return (prevValues as AddInternalUserRelationWithCustomerFilesType[]).map(
							({ user: prevUser, customerFiles: prevCustomerFiles }) =>
								prevUser.id === userId
									? {
											user: prevUser,
											customerFiles: prevCustomerFiles.filter(({ id }) => id !== removedCustomerFileId),
									  }
									: { user: prevUser, customerFiles: prevCustomerFiles },
						);
					}

					return (prevValues as AddInternalUserType[]).filter(({ id: prevValueId }) => prevValueId !== userId);
				});
			}
		},
	});

	const handleRemoveInternalUserCustomerFileRelationButtonClick = useCallback(
		async (id: InternalUserCustomerFileRelationType['id']) => {
			const isRemovalConfirmed = await confirmAction({
				title: 'Êtes-vous sûr de vouloir supprimer la relation utilisateur / dossier client ?',
				confirmButtonDetails: {
					color: 'error',
					label: 'Supprimer',
				},
			});

			if (!isRemovalConfirmed) return null;

			return removeInternalUserCustomerFileRelation({
				variables: {
					removeInternalUserCustomerFileRelationId: id,
				},
			});
		},
		[confirmAction, removeInternalUserCustomerFileRelation],
	);

	const columns: TableColumnType<InternalUserCustomerFileRelationType>[] = useMemo(
		() => [
			{
				flexGrow: 0,
				key: 'code',
				render: ({ customerFile: { code } }) => code,
				title: 'Code',
				width: 80,
			},
			{
				key: 'name',
				render: ({ customerFile: { name } }) => name,
				title: 'Nom du dossier',
				width: 250,
			},
			{
				key: 'chip',
				render: ({
					customerFile: {
						domain: { isDefault, name },
					},
				}) => <Chip label={name} color={isDefault ? 'info' : 'default'} />,
				title: 'Domaine',
				width: 150,
			},
			...(!isAdministrator
				? [
						{
							align: 'center',
							key: 'actions',
							flexGrow: 0,
							render: ({ id }) => (
								<IconButton onClick={() => handleRemoveInternalUserCustomerFileRelationButtonClick(id)}>
									<Icon path={mdiDelete} />
								</IconButton>
							),
							width: 40,
						} as TableColumnType<InternalUserCustomerFileRelationType>,
				  ]
				: []),
		],
		[handleRemoveInternalUserCustomerFileRelationButtonClick, isAdministrator],
	);

	const customerFileByUserDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: InternalUserCustomerFileRelationType[];
		}> => {
			const { data, error } = await fetchCustomerFilesByUser({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				internalUserCustomerFileRelationsByUser: { count = 0, items = [] },
			} = data ?? {
				internalUserCustomerFileRelationsByUser: {},
			};

			isRemoveInternalUserCustomerFileRelationCalled && resetRemoveInternalUserCustomerFileRelation();

			return {
				count,
				items,
			};
		},
		[
			fetchCustomerFilesByUser,
			isRemoveInternalUserCustomerFileRelationCalled,
			resetRemoveInternalUserCustomerFileRelation,
		],
	);

	const handleRelationUserIdSelection = useCallback(
		() => push(<AddUserCustomerFileRelationsForm user={user} />),
		[push, user],
	);

	// Reload the table if the query has been refetched
	useEffect(() => {
		if (networkStatus === NetworkStatus.refetch) {
			(async () => {
				await refetch();
				tableInstance.reload();
			})().catch((e) => {
				throw e;
			});
		}
	}, [tableInstance, networkStatus, refetch]);

	return (
		<SettingsTable
			addButtonLabel={!isAdministrator ? 'Ajout de dossiers' : null}
			dataSource={customerFileByUserDataSource}
			onAddButtonClick={!isAdministrator ? handleRelationUserIdSelection : undefined}
			table={tableInstance}
			tableColumns={columns}
			title="Dossiers"
		/>
	);
};

export default CustomerFilesByUserTable;
